import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
// @ts-ignore
import { Wheel } from 'https://cdn.jsdelivr.net/npm/spin-wheel@5.0.2/dist/spin-wheel-esm.js';
import styles from './WheelComponent.module.scss';
import WebApp from '@twa-dev/sdk';

interface WheelInstance {
  remove: () => void;
  spinToItem: (
    index: number,
    duration: number,
    clockwise: boolean,
    revolutions: number,
    easing: number,
  ) => void;
  onRest: (callback: () => void) => void;
}

const WHEEL_ITEMS = [
  { value: '10,0', type: 'STARS', color: 'rgba(255, 158, 14, 1)' },
  { value: '0,3', type: 'STARS', color: 'rgb(141,85,25)' },
  { value: '0,5', type: 'STARS', color: 'rgba(255, 158, 14, 1)' },
  { value: '1,0', type: 'STARS', color: 'rgb(141,85,25)' },
  { value: '2,0', type: 'STARS', color: 'rgba(255, 158, 14, 1)' },
  { value: '5,0', type: 'STARS', color: 'rgb(141,85,25)' },
];

const CANVAS_CONFIG = {
  width: 180,
  height: 35,
  iconSize: 30,
  padding: 8,
  mainFont: 'bold 32px Lato',
  subFont: 'bold 18px Lato',
  textColor: '#ffffff',
  scale: 2,
};

const WHEEL_CONFIG = {
  duration: 4000,
  minRevolutions: 5,
  maxExtraRevolutions: 3,
  imageRadius: 0.63,
  imageRotation: -90,
  imageScale: 1,
  itemLabelRadius: 10.58,
  rotationResistance: -35,
};

const createCurrencyImage = async (label: any, iconPath: any, text: any) => {
  await document.fonts.ready;
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.width = `${CANVAS_CONFIG.width}px`;
  tempDiv.style.height = `${CANVAS_CONFIG.height}px`;
  document.body.appendChild(tempDiv);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(CANVAS_CONFIG.width));
  svg.setAttribute('height', String(CANVAS_CONFIG.height));
  svg.setAttribute(
    'viewBox',
    `0 0 ${CANVAS_CONFIG.width} ${CANVAS_CONFIG.height}`,
  );

  return new Promise((resolve) => {
    fetch(iconPath)
      .then((response) => response.text())
      .then((svgContent) => {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_CONFIG.width * CANVAS_CONFIG.scale;
        canvas.height = CANVAS_CONFIG.height * CANVAS_CONFIG.scale;
        const ctx = canvas.getContext('2d');

        ctx!.imageSmoothingEnabled = true;
        ctx!.imageSmoothingQuality = 'high';

        ctx!.scale(CANVAS_CONFIG.scale, CANVAS_CONFIG.scale);

        ctx!.textBaseline = 'middle';
        ctx!.fillStyle = CANVAS_CONFIG.textColor;
        ctx!.textRendering = 'optimizeLegibility';
        ctx!.letterSpacing = '0.5px';

        // Сначала рисуем цифры (значения)
        ctx!.font = CANVAS_CONFIG.mainFont;
        const labelWidth = ctx!.measureText(label).width;

        // Вычисляем общую ширину контента (текст + отступ + иконка)
        const totalWidth =
          labelWidth + CANVAS_CONFIG.padding + CANVAS_CONFIG.iconSize;

        // Вычисляем начальную позицию для центрирования
        const startX = (CANVAS_CONFIG.width - totalWidth) / 2;

        // Рисуем текст по центру
        ctx!.fillText(label, startX, CANVAS_CONFIG.height / 2);

        // Убираем отображение типа (DC/USDT) - оставляем только значение и иконку звезды
        // const labelWidth = ctx!.measureText(label).width;
        // ctx!.font = CANVAS_CONFIG.subFont;
        // ctx!.fillText(
        //   text,
        //   CANVAS_CONFIG.iconSize +
        //     CANVAS_CONFIG.padding +
        //     labelWidth +
        //     CANVAS_CONFIG.padding,
        //   CANVAS_CONFIG.height / 2,
        // );

        const iconGroup = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'g',
        );
        iconGroup.innerHTML = svgContent;

        const iconElement = iconGroup.firstElementChild;
        iconElement?.setAttribute('width', String(CANVAS_CONFIG.iconSize));
        iconElement?.setAttribute('height', String(CANVAS_CONFIG.iconSize));

        // Позиционируем иконку после цифр по центру
        iconElement?.setAttribute(
          'x',
          String(startX + labelWidth + CANVAS_CONFIG.padding),
        );
        // Центрируем иконку по вертикали относительно текста
        const iconY = (CANVAS_CONFIG.height - CANVAS_CONFIG.iconSize) / 2;
        iconElement?.setAttribute('y', String(iconY));

        svg.appendChild(iconGroup);

        const image = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'image',
        );
        image.setAttribute('x', '0');
        image.setAttribute('y', '0');
        image.setAttribute('width', String(CANVAS_CONFIG.width));
        image.setAttribute('height', String(CANVAS_CONFIG.height));
        image.setAttribute('href', canvas.toDataURL('image/png', 1.0));
        svg.appendChild(image);

        const svgString = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const resultImage = new Image();
        resultImage.onload = () => {
          URL.revokeObjectURL(url);
          document.body.removeChild(tempDiv);
          resolve(resultImage);
        };
        resultImage.src = url;
      });
  });
};

const WheelComponent = forwardRef(
  ({ onSpin, onSpinComplete, onInitialized }: any, ref: any) => {
    const wheelContainerRef = useRef(null);
    const wheelInstanceRef = useRef<WheelInstance | null>(null);
    const isInitializedRef = useRef(false);

    const createWheelItems = useCallback(async () => {
      try {
        const items = await Promise.all(
          WHEEL_ITEMS.map(async ({ value, type, color }) => {
            // Используем иконку звезды для всех секторов вместо USDT/DC
            let iconPath = '';
            if (color === 'rgba(255, 158, 14, 1)') {
              iconPath = '/iconBrown.svg';
            } else {
              iconPath = '/star.svg';
            }
            const image = await createCurrencyImage(value, iconPath, type);
            return {
              backgroundColor: color,
              labelColor: CANVAS_CONFIG.textColor,
              image,
              imageRadius: WHEEL_CONFIG.imageRadius,
              imageRotation: WHEEL_CONFIG.imageRotation,
              imageScale: WHEEL_CONFIG.imageScale,
            };
          }),
        );
        return items;
      } catch (error) {
        // console.error('Ошибка создания элементов колеса:', error);
        return [];
      }
    }, []);

    const initializeWheel = useCallback(async () => {
      if (!wheelContainerRef.current || isInitializedRef.current) return;

      try {
        const items = await createWheelItems();
        if (wheelInstanceRef.current) {
          wheelInstanceRef.current.remove();
        }

        wheelInstanceRef.current = new Wheel(wheelContainerRef.current, {
          items,
          itemLabelFont: 'Lato, sans-serif',
          itemLabelFontSizeMax: 24,
          itemLabelRadius: WHEEL_CONFIG.itemLabelRadius,
          lineWidth: 1,
          borderColor: '#ffffff',
          radius: 1,
          rotationResistance: WHEEL_CONFIG.rotationResistance,
          itemLabelAlign: 'right',
          onRest: () => onSpinComplete?.(),
          isInteractive: false,
          borderWidth: 0,
        });

        isInitializedRef.current = true;
        onInitialized?.();
      } catch (error) {
        // console.error('Ошибка инициализации колеса:', error);
      }
    }, [createWheelItems, onSpinComplete, onInitialized]);

    useEffect(() => {
      initializeWheel();

      return () => {
        if (wheelInstanceRef.current) {
          wheelInstanceRef.current.remove();
          wheelInstanceRef.current = null;
        }
        isInitializedRef.current = false;
      };
    }, [initializeWheel]);

    const handleSpin = useCallback(
      (targetIndex: number) => {
        if (!wheelInstanceRef.current) return;

        const revolutions =
          WHEEL_CONFIG.minRevolutions +
          Math.floor(Math.random() * WHEEL_CONFIG.maxExtraRevolutions);

        const totalSectors = WHEEL_ITEMS.length;
        const totalSteps = revolutions * totalSectors + targetIndex;
        const interval = WHEEL_CONFIG.duration / totalSteps;

        let currentStep = 0;

        const tickInterval = setInterval(() => {
          currentStep++;

          if (WebApp && WebApp.HapticFeedback) {
            WebApp.HapticFeedback.notificationOccurred('success');
          }

          if (currentStep >= totalSteps) {
            clearInterval(tickInterval);
          }
        }, interval);

        wheelInstanceRef.current.spinToItem(
          targetIndex,
          WHEEL_CONFIG.duration,
          true,
          revolutions,
          1,
        );

        onSpin?.(targetIndex);
        wheelInstanceRef.current.onRest = () => onSpinComplete?.(targetIndex);
      },
      [onSpin, onSpinComplete],
    );

    useImperativeHandle(ref, () => ({
      spin: handleSpin,
      isInitialized: () => isInitializedRef.current,
    }));

    return (
      <div className={styles.wheelWrapper}>
        <div className={styles.gradientBackground}></div>
        <div ref={wheelContainerRef} className={styles.wheelContainer}>
          <img src={'/flapper.svg'} alt="Pointer" className={styles.pointer} />
          <div className={styles.centerCircleShadow}></div>
          <div className={styles.centerCircle}></div>
        </div>
      </div>
    );
  },
);

WheelComponent.displayName = 'WheelComponent';

export default WheelComponent;
